import { apiClient } from '@borradh-workspace/api-client';
import type { ClientPackage } from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { invalidateKeys, queryKeys } from '@/lib/query-keys';

/** Packages a customer bought, newest first, with the sessions left on each. */
export const clientPackagesQueryOptions = (leadId: string) =>
  queryOptions({
    queryKey: queryKeys.clientPackages.byLead(leadId),
    queryFn: () => {
      const params = new URLSearchParams({ leadId });
      return apiClient.get<ClientPackage[]>(
        `client-packages?${params.toString()}`
      );
    },
    enabled: !!leadId,
    staleTime: 30 * 1000,
  });

export const useClientPackages = (leadId: string) => {
  const query = useQuery(clientPackagesQueryOptions(leadId));
  return {
    packages: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Staff actions on a bought package: `activate` once payment was taken in
 * clinic, `cancel` to void the sessions left (any refund is done in Stripe).
 */
export const useChangeClientPackage = (options: { leadId: string }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      clientPackageId,
      action,
    }: {
      clientPackageId: string;
      action: 'activate' | 'cancel';
    }) =>
      apiClient.post<ClientPackage>(
        `client-packages/${clientPackageId}/${action}`,
        {}
      ),
    onSuccess: (_pkg, { action }) => {
      invalidateKeys(
        queryClient,
        queryKeys.clientPackages.byLead(options.leadId)
      );
      toast.success(
        action === 'activate' ? 'Package activated' : 'Package cancelled'
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update the package');
    },
  });
  return {
    changeClientPackage: mutation.mutate,
    isChanging: mutation.isPending,
  };
};
